import { Divider, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { NATIVE_CHAIN_LAUNCH_TICKER } from '@/constants/blockchain'
import useSessionStorage from '@/hooks/useSessionStorage'
import { useAuth } from '@/plugins/auth'
import { processBalance } from '@/utils/utils'

import RefreshButton from '../Buttons/RefreshButton'
import RootButton from '../Buttons/RootButton'
import ClaimAirdropModal from '../Modals/Airdrop/ClaimAirdropModal'
import ModalSkeleton from '../Modals/ModalSkeleton'
import TooltipTitle from '../TooltipTitle'
import { getAirdropRewards } from './CheckHubAirdop'
import { getDiscordRewards } from './LinkDiscordAirdrop'
import NeedHelp from './NeedHelp'
import { AirdropRecipient } from './utils'

interface RewardsAirdropProps {
  prevStep: () => void
  nextStep: () => void
}

const RewardsAirdrop: React.FC<RewardsAirdropProps> = ({ prevStep, nextStep }) => {
  const theme = useMantineTheme()
  const { t } = useTranslation()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const { address } = useAuth()
  const [rewards, setRewards] = useSessionStorage<AirdropRecipient[]>(`addressRewards-${address}`)
  const [discordRewards, setDiscordRewards] = useSessionStorage<AirdropRecipient[]>(
    `discordRewards-${address}`
  )
  const [, setMergedRewards] = useSessionStorage<AirdropRecipient[]>(`mergedRewards-${address}`, [])
  const [opened, { open, close }] = useDisclosure()
  const [refreshingRewards, setRefreshingRewards] = useState<boolean>(false)

  const refreshRewards = useCallback(async () => {
    setRefreshingRewards(true)
    console.log('refreshing address rewards')
    if (!address) return
    try {
      const addrRewards = await getAirdropRewards(address)
      setRewards(addrRewards)
    } catch (error) {
      console.error(error)
    }
    console.log('refreshing discord rewards')
    try {
      const dsRewards = await getDiscordRewards(address, setRewards)
      setDiscordRewards(dsRewards)
    } catch (error) {
      console.error(error)
    }
    setRefreshingRewards(false)
  }, [])

  // remove all the discord rewards that are have the same AirdropID and NodeID than the address rewards and then merge the two arrays
  const processDsRewards = useMemo(() => {
    if (rewards && discordRewards) {
      return discordRewards.filter(
        discordReward =>
          !rewards.some(
            reward =>
              reward.AirdropID === discordReward.AirdropID && reward.NodeID === discordReward.NodeID
          )
      )
    }
    if (!rewards && discordRewards) return discordRewards
    return []
  }, [rewards, discordRewards])

  const totalDiscordRewards = useMemo(() => {
    if (processDsRewards) {
      return processDsRewards
        .map(reward => reward.RewardAmount)
        .reduce((acc, val) => BigInt(acc) + BigInt(val), BigInt(0))
    }
    return BigInt(0)
  }, [processDsRewards])

  const totalAddressRewards = useMemo(() => {
    if (rewards) {
      return rewards
        .map(reward => reward.RewardAmount)
        .reduce((acc, val) => BigInt(acc) + BigInt(val), BigInt(0))
    }
    return BigInt(0)
  }, [rewards])

  useEffect(() => {
    setMergedRewards(rewards && discordRewards ? processDsRewards.concat(rewards) : [])
  }, [rewards, discordRewards])

  const totalRewards = useMemo(
    () => BigInt(totalAddressRewards) + BigInt(totalDiscordRewards),
    [totalAddressRewards, totalDiscordRewards]
  )

  return (
    <Stack style={{ gap: 40 }}>
      <Divider />
      <Stack style={{ gap: '20px' }}>
        <Title order={isMobile ? 3 : 2}>{t('Modal', 'Review and claim rewards')}</Title>
        <Text style={{ fontSize: 14 }}>
          {t(
            'components.airdrop.rewardsClaimDescription',
            'If all looks good, claim your platform and community rewards below:'
          )}
        </Text>
        <Stack style={{ gap: '20px' }}>
          <Stack style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <TooltipTitle
              title={t('components.airdrop.onChain', 'Platform Rewards')}
              tooltip={t(
                'components.airdrop.onChainTooltip',
                `Platform rewards are linked to your LAMINA1 Hub and Telegram activity during the first 6 months of Mainnet. Relevant activity includes mainnet transactions, test platform transactions, items published, smart contracts deployed, and other factors. This amount also includes Telegram questing XP and any Rewards Pass multipliers applied to your account.`
              )}
            />
            <Text style={{ fontFamily: 'monument-grotesk-heavy, sans-serif' }}>
              {processBalance(totalAddressRewards).display} {NATIVE_CHAIN_LAUNCH_TICKER}
            </Text>
          </Stack>
          <Stack style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <TooltipTitle
              title={t('components.airdrop.community', 'Community Rewards')}
              tooltip={t(
                'components.airdrop.communityTooltip',
                `Community rewards are rewards linked to your Discord and Zealy activity during the LAMINA1 Testnet and Betanet. Relevant activity includes: Discord badges, messaging activity, Zealy leaderboard status, and participation in key LAMINA1 initiatives.`
              )}
            />
            <Text style={{ fontFamily: 'monument-grotesk-heavy, sans-serif' }}>
              {processBalance(totalDiscordRewards).display} {NATIVE_CHAIN_LAUNCH_TICKER}
            </Text>
          </Stack>
          <Divider />
          <Stack style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <TooltipTitle
              title={t('components.airdrop.total', 'Total Rewards')}
              tooltip={t(
                'components.airdrop.totalTooltip',
                `Your combined platform and community rewards. This amount should be the sum of the previous two numbers.`
              )}
            />
            <Text style={{ fontFamily: 'monument-grotesk-heavy, sans-serif' }}>
              {processBalance(totalRewards).display} {NATIVE_CHAIN_LAUNCH_TICKER}
            </Text>
          </Stack>
          <RootButton
            secondary
            onClick={refreshRewards}
            expand
            disabled={refreshingRewards}
            style={{
              color: theme.colors.paragraph[0],
              width: 'auto',
            }}
            leftSection={<RefreshButton noButton loading={refreshingRewards} />}
          >
            {refreshingRewards
              ? t('components.airdrop.refreshingRewards', 'Refreshing Rewards...')
              : t('buttons.refreshClaim', 'Refresh Rewards')}
          </RootButton>
        </Stack>
        <Stack style={{ flexDirection: 'row' }}>
          <RootButton secondary onClick={prevStep} disabled={refreshingRewards}>
            {t('buttons.back', 'Back')}
          </RootButton>
          <RootButton onClick={open} expand disabled={refreshingRewards}>
            {t('buttons.completeClaim', 'Complete Claim')}
          </RootButton>
        </Stack>
        <NeedHelp address review />
      </Stack>
      <ModalSkeleton
        title={t('components.airdrop.rewardsClaim', 'Confirm Claim')}
        opened={opened}
        onClose={close}
      >
        <ClaimAirdropModal claimableRewards={totalRewards} close={close} nextStep={nextStep} />
      </ModalSkeleton>
    </Stack>
  )
}
export default RewardsAirdrop
